import { useState, useEffect } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { IMassaTotal, IMetric } from "./AppCampaign.types";
import { cpfMask, formatCPF } from "utils/cpf";
import { Box, Button, CircularProgress, Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useAlert } from "contexts/alert";
import api from "services/api";
import { useParams } from "react-router-dom";

type Props = {
  beneficiaries: IMassaTotal[];
};

/**
 * Default = traz sort pelo CPF titular
 * Ao selecionar um titular q tem dependentes, aparece interface q selecionou dependentes
 * Ao filtrar, perde os selecionados
 */
export function AppCampaignSend({ beneficiaries }: Props) {
  const { workspaceActivityId } = useParams() as { workspaceActivityId: string; };
  const { openAlert } = useAlert();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedWithoutEmail, setSelectedWithoutEmail] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [metrics, setMetrics] = useState<IMetric[]>([
    { label: "Total", value: String(0) },
    { label: "Titulares", value: String(0) },
    { label: "Dependentes", value: String(0) },
    { label: "PJs", value: String(0) },
    { label: "CLTs", value: String(0) },
  ]);

  const formatPersonType = (type: "employee" | "dependent") => {
    return type === "employee" ? "Titular" : "Dependente";
  };

  async function handleSendEmail() {
    try {
      setIsSending(true);
      setSelectedWithoutEmail([]);

      const selected = selectedIds.map(id => {
        const beneficiary = beneficiaries.find(person => person.ID === id);
        if (beneficiary) return { email: beneficiary.EMAIL, taxPayerId: beneficiary.TAX_PAYER_ID };
        return null;
      }).filter(b => !!b);

      /** Validacao se tem ou nao email e CPF!! */
      const withoutEmail = selected.filter(b => !b?.email).map(b => b?.taxPayerId);

      if (!!withoutEmail.length) {
        //@ts-ignore
        setSelectedWithoutEmail(withoutEmail);
        setIsModalOpen(true);
        throw new Error("Os seguintes CPFs selecionados não tem email para envio de campanha.");
      }

      await api.post(`/v2/apps/campaign/${workspaceActivityId}`, {
        data: {
          campaignType: "MISSING_DATA",
          beneficiaries: selected,
        },
      });
      
      setSelectedIds([]);
      setSelectedWithoutEmail([]);

      openAlert("Campanha enviada com sucesso.", "success");
      // window.location.reload();
    } catch(err: any) {
      const message = err?.message || "Algo deu errado ao enviar a campanha."
      openAlert(message, "error");
    } finally {
      setIsSending(false);
    }
  }

  const columns: GridColDef<IMassaTotal>[] = [
    {
      field: "NAME",
      headerName: "Nome do beneficiário",
      width: 250,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "TAX_PAYER_ID",
      headerName: "CPF do beneficiário",
      width: 150,
      renderCell: (params) => cpfMask(params.value),
    },
    {
      field: "EMPLOYEE_TAX_PAYER_ID", 
      headerName: "CPF do titular",
      width: 150,
      renderCell: (params) => cpfMask(params.value),
    },
    {
      field: "LEGAL_ENTITY_TAX_ID",
      headerName: "CNPJ",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "PERSON_TYPE",
      headerName: "Tipo do beneficiário", 
      width: 150,
      renderCell: (params) => formatPersonType(params.value),
    },
    {
      field: "missingData",
      headerName: "Dados faltantes",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMAIL",
      headerName: "E-mail de envio",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "PHONE_NUMBER",
      headerName: "Telefone de envio",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
  ];

  const rows = beneficiaries.map((beneficiary) => ({
    id: beneficiary.ID,
    ...beneficiary,
  }));

  useEffect(() => {
    (async () => {
      const selectedBeneficiaries = selectedIds.map(id => {
        const person = beneficiaries.find(beneficiary => beneficiary.ID === id);

        return {
          taxPayerId: person?.TAX_PAYER_ID ?? null,
          type: person?.PERSON_TYPE ?? null,
          isPj: !!person?.LEGAL_ENTITY_TAX_ID ? true : false,
        }
      })
      .filter(beneficiary => beneficiary.isPj !== null && !!beneficiary.taxPayerId)
      .flatMap(beneficiary => {
        const dependents = beneficiaries.filter(b => b.EMPLOYEE_TAX_PAYER_ID === beneficiary.taxPayerId && b.PERSON_TYPE === "dependent");

        return [
          beneficiary,
          ...dependents.map(d => ({
            taxPayerId: d.TAX_PAYER_ID,
            type: "dependent",
            isPj: beneficiary.isPj ? true : false,
          })),
        ]
      })

      setMetrics([
        { label: "Total", value: String(selectedBeneficiaries.length) },
        { label: "Titulares", value: String(selectedBeneficiaries.filter(b => b.type === "employee").length) },
        { label: "Dependentes", value: String(selectedBeneficiaries.filter(b => b.type === "dependent").length) },
        { label: "PJs", value: String(selectedBeneficiaries.filter(b => b.isPj === true).length) },
        { label: "CLTs", value: String(selectedBeneficiaries.filter(b => b.isPj === false).length) },
      ])
    })()
  }, [selectedIds])

  return (
    <>
      <Modal open={isModalOpen}>
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            height: "auto",
            width: "100%",
            maxWidth: 700,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: "1rem",
            border: "none",
            outline: "none",
            padding: "24px",
          }}
        >
        <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
          <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Erro ao enviar campanha</h6>
          <Close
            style={{"cursor":"pointer"}}
            onClick={() => setIsModalOpen(false)}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '24px' }}>
          {<span style={{ fontSize: '16px', lineHeight: '150%', fontWeight: '400', color: '#191C1A' }}>Não foi possível enviar a campanha pois os seguintes CPFs não possuem e-mail para envio. Alteração disponível no APP de edição de dados.</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '24px' }}>
          {!!selectedWithoutEmail.length && <span style={{ fontSize: '16px', lineHeight: '150%', fontWeight: '400', color: '#191C1A' }}><strong>{selectedWithoutEmail.map(taxPayerId => cpfMask(taxPayerId)).join(", ")}</strong>.</span>}
        </div>

        <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
          <div style={{"display":"flex","gap":"0.25rem"}}>
            <Button
              variant='outlined'
              color='error'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setIsModalOpen(false)}
              disabled={isSending}
            >
              Verificar novamente
            </Button>

            {/* <Button
              variant='contained'
              style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setIsModalOpen(false)}
            >
              Confirmar
            </Button> */}
          </div>
        </div>
        </Box>
      </Modal>
      <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "16px" }}>
        {metrics.map(metric => (
          <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', marginBottom: '30px' }}>
            <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>{metric.label}</p>
            <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{metric.value}</span>
          </div>
        ))}
      </div>
      <DataGrid
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        isRowSelectable={(params) => params.row.PERSON_TYPE === "employee"}
        // rowSelectionModel={selectedIds}
        onRowSelectionModelChange={(selected) => {
          const selectedIds: string[] = [];
          selected.ids.forEach(id => selectedIds.push(String(id)));
          setSelectedIds(selectedIds);
        }}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        style={{ backgroundColor: "white", minHeight: 400 }}
        disableColumnFilter={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
        scrollbarSize={10}
        sx={{
          "& .MuiDataGrid-row[data-rowselectable='false']": {
            "& .MuiCheckbox-root": {
              opacity: 0.3,
              cursor: "not-allowed",
            },
            "& .MuiCheckbox-root.Mui-disabled": {
              opacity: 0.3,
              cursor: "not-allowed",
            },
          },
          "& .MuiDataGrid-checkboxInput.Mui-disabled": {
            opacity: 0.3,
            cursor: "not-allowed",
          },
        }}
      />
      <div style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "end" }}>
        <Button
          variant="contained"
          style={{
            paddingTop: "0.25rem",
            paddingBottom: "0.25rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
            borderRadius: "0.375rem",
            height: "2.5rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            fontWeight: 600,
            width: "200px",
            marginLeft: "50px"
          }}
          onClick={handleSendEmail}
          disabled={!selectedIds.length || isSending}
        >
          {!isSending ? "Enviar campanha" : <CircularProgress size="20px"/>}
        </Button>
      </div>
    </>
  );
}
